import axios from "axios";
import axiosInstance from "@/api/axiosInstance";

// Re-export types from the new types file for backward compatibility
export type {
  EvaluationCriterion,
  SubCriterion,
  ScoringRubric,
  ScoringScale,
  CreateRubricRequest,
  UpdateRubricRequest,
  LegacyEvaluationCriterion,
} from "../types/rubricTypes";

// Import types for use in this file
import type {
  EvaluationCriterion,
  ScoringRubric,
  CreateRubricRequest,
  UpdateRubricRequest,
} from "../types/rubricTypes";

export interface Employee {
  id: string;
  firstName: string;
  lastName: string;
  email: string;
  role: string;
  employeeRole?: "SALES_REP" | "COACH" | "SUPPORT" | "MANAGER";
  hasCustomRubric?: boolean;
}

export interface GeneratePromptRequest {
  userInput: string;
  criteria: EvaluationCriterion[];
  scale: { min: number; max: number };
}

export interface GeneratePromptResponse {
  prompt?: string;
  error?: string;
}

export const salesCoachingScoringSettingsApi = {
  // Rubric CRUD operations
  fetchAllRubrics: async (): Promise<ScoringRubric[]> => {
    try {
      const response = await axiosInstance.get<ScoringRubric[]>(
        `/api/sales-coaching/rubrics`,
      );
      return response.data;
    } catch {
      // Mock data for testing - includes new enhanced fields
      return [
        {
          id: "1",
          name: "Default Sales Rubric",
          type: "SALES",
          isDefault: true,
          globalTemplate: "Evaluate sales performance...",
          prompt: "Evaluate sales performance...",
          evaluationCriteria: [
            {
              id: "1",
              name: "Opening Effectiveness",
              description:
                "How well did the rep establish rapport and set the agenda?",
              enabled: true,
              order: 1,
              weight: 15,
            },
            {
              id: "2",
              name: "Needs Discovery",
              description:
                "How effectively did the rep uncover customer needs and pain points?",
              enabled: true,
              order: 2,
              weight: 20,
            },
            {
              id: "3",
              name: "Product Knowledge",
              description:
                "How well did the rep demonstrate understanding of products/services?",
              enabled: true,
              order: 3,
              weight: 15,
            },
            {
              id: "4",
              name: "Objection Handling",
              description:
                "How skillfully did the rep address concerns and objections?",
              enabled: true,
              order: 4,
              weight: 15,
            },
            {
              id: "5",
              name: "Closing Technique",
              description:
                "How effectively did the rep move toward a commitment or next step?",
              enabled: true,
              order: 5,
              weight: 15,
            },
            {
              id: "6",
              name: "Customer Engagement",
              description:
                "How well did the rep maintain engagement and build relationship?",
              enabled: true,
              order: 6,
              weight: 10,
            },
            {
              id: "7",
              name: "Follow-up Commitment",
              description:
                "How clearly were next steps defined and agreed upon?",
              enabled: true,
              order: 7,
              weight: 10,
            },
          ],
          scoringScale: { min: 0, max: 100 },
          maxPossibleScore: 100,
          active: true,
          version: 1,
          updatedAt: new Date().toISOString(),
          updatedBy: "System",
        },
        {
          id: "2",
          name: "Default Coach Rubric",
          type: "COACH",
          isDefault: true,
          globalTemplate: "Evaluate coaching effectiveness...",
          prompt: "Evaluate coaching effectiveness...",
          evaluationCriteria: [
            {
              id: "1",
              name: "Active Listening",
              description:
                "How well did the coach listen and respond to the coachee?",
              enabled: true,
              order: 1,
              weight: 20,
            },
            {
              id: "2",
              name: "Questioning Technique",
              description:
                "How effectively did the coach use open-ended questions?",
              enabled: true,
              order: 2,
              weight: 15,
            },
            {
              id: "3",
              name: "Feedback Delivery",
              description:
                "How constructive and specific was the feedback provided?",
              enabled: true,
              order: 3,
              weight: 20,
            },
            {
              id: "4",
              name: "Goal Setting",
              description: "How clearly were development goals established?",
              enabled: true,
              order: 4,
              weight: 15,
            },
            {
              id: "5",
              name: "Action Planning",
              description: "How concrete and achievable were the action items?",
              enabled: true,
              order: 5,
              weight: 15,
            },
            {
              id: "6",
              name: "Motivation & Support",
              description: "How well did the coach inspire and encourage?",
              enabled: true,
              order: 6,
              weight: 15,
            },
          ],
          scoringScale: { min: 0, max: 100 },
          maxPossibleScore: 100,
          active: true,
          version: 1,
          updatedAt: new Date().toISOString(),
          updatedBy: "System",
        },
      ];
    }
  },

  fetchRubric: async (id: string): Promise<ScoringRubric> => {
    const response = await axiosInstance.get<ScoringRubric>(
      `/api/sales-coaching/rubrics/${id}`,
    );
    return response.data;
  },

  createRubric: async (rubric: CreateRubricRequest): Promise<ScoringRubric> => {
    const response = await axiosInstance.post<ScoringRubric>(
      `/api/sales-coaching/rubrics`,
      rubric,
    );
    return response.data;
  },

  updateRubric: async (
    id: string,
    rubric: UpdateRubricRequest,
  ): Promise<ScoringRubric> => {
    const response = await axiosInstance.put<ScoringRubric>(
      `/api/sales-coaching/rubrics/${id}`,
      rubric,
    );
    return response.data;
  },

  deleteRubric: async (id: string): Promise<void> => {
    await axiosInstance.delete(`/api/sales-coaching/rubrics/${id}`);
  },

  // Employee operations
  fetchEmployees: async (): Promise<Employee[]> => {
    try {
      const response = await axiosInstance.get<Employee[]>(
        `/api/sales-coaching/employees`,
      );
      return response.data;
    } catch {
      // Mock data for testing
      return [
        {
          id: "emp1",
          firstName: "John",
          lastName: "Doe",
          email: "john.doe@example.com",
          role: "Sales Representative",
          employeeRole: "SALES_REP",
          hasCustomRubric: false,
        },
        {
          id: "emp2",
          firstName: "Jane",
          lastName: "Smith",
          email: "jane.smith@example.com",
          role: "Sales Manager",
          employeeRole: "MANAGER",
          hasCustomRubric: true,
        },
        {
          id: "emp3",
          firstName: "Bob",
          lastName: "Johnson",
          email: "bob.johnson@example.com",
          role: "Coach",
          employeeRole: "COACH",
          hasCustomRubric: false,
        },
      ];
    }
  },

  updateEmployeeRole: async (
    employeeId: string,
    role: string,
  ): Promise<Employee> => {
    const response = await axiosInstance.patch<Employee>(
      `/api/sales-coaching/employees/${employeeId}/role`,
      { employeeRole: role },
    );
    return response.data;
  },

  // Role-based rubric operations
  fetchRoleRubrics: async (): Promise<ScoringRubric[]> => {
    try {
      const response = await axiosInstance.get<ScoringRubric[]>(
        `/api/sales-coaching/rubrics/role-defaults`,
      );
      return response.data;
    } catch {
      // Return same mock data as fetchAllRubrics for testing
      return salesCoachingScoringSettingsApi.fetchAllRubrics();
    }
  },

  setDefaultRubricForRole: async (
    type: string,
    rubricId: string,
  ): Promise<ScoringRubric> => {
    const response = await axiosInstance.post<ScoringRubric>(
      `/api/sales-coaching/rubrics/set-default`,
      { type, rubricId },
    );
    return response.data;
  },

  // Legacy support - redirect to rubric operations
  fetchSettings: async (): Promise<ScoringRubric> => {
    const rubrics = await salesCoachingScoringSettingsApi.fetchRoleRubrics();
    const defaultSalesRubric = rubrics.find(
      (r) => r.type === "SALES" && r.isDefault,
    );

    if (defaultSalesRubric) {
      return defaultSalesRubric;
    }

    // Return default if none exist
    return {
      id: "default",
      name: "Default Sales Rubric",
      type: "SALES",
      isDefault: true,
      prompt: `Analyze this sales call transcript and evaluate the sales representative's performance.

Provide a score for each category and an overall score (average of all categories).
Include specific examples from the transcript to support each score.
Identify 2-3 key strengths and 2-3 areas for improvement with actionable coaching recommendations.`,
      evaluationCriteria: [
        {
          id: "1",
          name: "Opening Effectiveness",
          description:
            "How well did the rep establish rapport and set the agenda?",
          enabled: true,
          order: 1,
          weight: 20,
        },
        {
          id: "2",
          name: "Needs Discovery",
          description:
            "How effectively did the rep uncover customer needs and pain points?",
          enabled: true,
          order: 2,
          weight: 25,
        },
        {
          id: "3",
          name: "Product Knowledge",
          description:
            "How well did the rep demonstrate understanding of products/services?",
          enabled: true,
          order: 3,
          weight: 20,
        },
        {
          id: "4",
          name: "Objection Handling",
          description:
            "How skillfully did the rep address concerns and objections?",
          enabled: true,
          order: 4,
          weight: 20,
        },
        {
          id: "5",
          name: "Closing Technique",
          description:
            "How effectively did the rep move toward a commitment or next step?",
          enabled: true,
          order: 5,
          weight: 15,
        },
      ],
      scoringScale: {
        min: 0,
        max: 10,
      },
      maxPossibleScore: 100,
      active: true,
      version: 1,
      updatedAt: new Date().toISOString(),
      updatedBy: "System",
    };
  },

  updateSettings: async (
    settings: UpdateRubricRequest,
  ): Promise<ScoringRubric> => {
    // Find and update the default sales rubric
    const rubrics = await salesCoachingScoringSettingsApi.fetchRoleRubrics();
    const defaultSalesRubric = rubrics.find(
      (r) => r.type === "SALES" && r.isDefault,
    );

    if (defaultSalesRubric) {
      return await salesCoachingScoringSettingsApi.updateRubric(
        defaultSalesRubric.id,
        settings,
      );
    }

    // Create new if none exist
    const newRubric: CreateRubricRequest = {
      name: "Default Sales Rubric",
      type: "SALES",
      isDefault: true,
      prompt: settings.prompt || "",
      evaluationCriteria: settings.evaluationCriteria || [],
      scoringScale: settings.scoringScale || { min: 0, max: 10 },
    };

    return await salesCoachingScoringSettingsApi.createRubric(newRubric);
  },

  // AI Prompt Generation
  generateScoringPrompt: async (
    request: GeneratePromptRequest,
  ): Promise<GeneratePromptResponse> => {
    try {
      const response = await axiosInstance.post<GeneratePromptResponse>(
        `/api/private/ai/generate-scoring-prompt`,
        request,
      );
      return response.data;
    } catch (error) {
      if (axios.isAxiosError(error) && error.response?.data?.error) {
        return { error: error.response.data.error };
      }
      return { error: "Failed to generate prompt. Please try again." };
    }
  },
};
